import { useCallback, useEffect, useState } from 'react';
import { FormikHelpers } from 'formik';
import { useNavigate } from 'react-router';
import {
  useAffiliate,
  useApplicationQuery,
  useLegacyLoginAuth,
  useCreateApplicationWithoutRiskAndSendGACookiesMutation,
  useExternalConsentsDataQuery,
  usePrecheckApplicationMutation,
  useRegistration,
} from 'hooks/webapi';
import useCalculator from 'hooks/calculator/useCalculator';
import { Consent } from 'hooks/webapi/useRegistration';
import { useClientApplicationCheckStatusQuery } from 'hooks/webapi/client/useClientApplicationCheckStatusQuery';
import useIovation from 'hooks/useIovation';
import { isAlreadyExistErrorMsg } from 'utils/errorMessageValidation';
import { DASHBOARD_LOGIN, PREFIX_PHONE } from '../../../constants';
import { PersonalDetailsFormValues } from './PersonalDetailsFormValues';
import { CreateApplicationOptions } from 'hooks/webapi/client/useCreateApplicationMutation';
import {
  ValidationError,
  formatBackendValidationErrors,
  scrollToElement,
} from 'utils';
import {
  PHONE_VERIFICATION,
  REJECTED_OFFER,
  MANUAL_REVIEW,
} from 'Router/routes';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import { getClientEmail } from 'hooks/webapi/client/useClientEmailQuery';
import { getClientPhone } from 'hooks/webapi/client/useClientPhoneQuery';
import IdentifyEvent from 'hooks/context/EventCollector/events/IdentifyEvent';
import getClient from 'api/getClient';
import useSeon from 'hooks/useSeon';

export default function useLegacyPersonalDetails() {
  const [pending, setPending] = useState(false);
  const { login } = useLegacyLoginAuth();
  const { partialRegister } = useRegistration();
  const { mutateAsync: precheckApplication } = usePrecheckApplicationMutation();
  const { mutateAsync: createClientApplicationWithoutRisk } =
    useCreateApplicationWithoutRiskAndSendGACookiesMutation();
  const { offer } = useCalculator();
  const [isPollingApplicationStatus, setIsPollingApplicationStatus] =
    useState(false);
  const { data: applicationCheckStatusData } =
    useClientApplicationCheckStatusQuery(isPollingApplicationStatus, 3000);
  const { data: applicationData } = useApplicationQuery(
    applicationCheckStatusData?.completed,
  );
  const navigate = useNavigate();
  const { ioBlackBox } = useIovation();
  const { identify } = useEventCollector();
  const { sendSeonFraudInfo } = useSeon();

  const { affiliate, affiliateForm } = useAffiliate();
  const [isAccountAlreadyExistsError, setIsAccountAlreadyExistsError] =
    useState(false);
  const { data: availableConsents, isLoading } = useExternalConsentsDataQuery();

  const handleSubmit = useCallback(
    async (
      values: PersonalDetailsFormValues,
      actions: FormikHelpers<PersonalDetailsFormValues>,
    ) => {
      try {
        setPending(true);
        // const recaptchaToken = await handleRecaptchaVerify();
        const { email, password } = values;

        const sanitizedValues: PersonalDetailsFormValues & {
          consents: Consent[];
        } = {
          ...values,
          mobilePhone: values.mobilePhone
            .replace(/ /g, '')
            .replace(PREFIX_PHONE, ''),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          passwordRepeat: values.password,
          consents: [
            {
              name: 'ACCEPT_CREDIT_AGREEMENT',
              accepted: values.acceptAgreement,
              version: availableConsents?.ACCEPT_CREDIT_AGREEMENT.version,
            },
            {
              name: 'UNDERSTANDING_AGREEMENT_CONFIRMED',
              accepted: values.acceptProfiling,
              version:
                availableConsents?.UNDERSTANDING_AGREEMENT_CONFIRMED.version,
            },
            {
              name: 'ACCEPT_NEWS',
              accepted: values.acceptNews,
              version: availableConsents?.ACCEPT_NEWS.version,
            },
          ],
        };
        await partialRegister({ body: sanitizedValues } /* recaptchaToken */);
        await login(email, password);

        const [client, emailData, phone] = await Promise.all([
          getClient(),
          getClientEmail(),
          getClientPhone(),
        ]);

        identify(
          new IdentifyEvent({
            phone,
            email: emailData,
            client,
          }),
        );

        if (offer) {
          const applicationOptions: CreateApplicationOptions = {
            amount: offer.newPrincipal,
            term: offer.term,
            type: 'WEB',
            source: 'DESKTOP',
            affiliateInfo: values.affiliateInfo,
            ioBlackBox: values.ioBlackBox,
          };
          await createClientApplicationWithoutRisk(applicationOptions);

          const precheck = await precheckApplication();
          if (precheck.rejected) {
            navigate(REJECTED_OFFER);
            return;
          }

          await sendSeonFraudInfo();
          setIsPollingApplicationStatus(true);
        }
      } catch (e: unknown) {
        if (
          e !== null &&
          e instanceof ValidationError &&
          e?.fieldErrors?.length
        ) {
          const isAlreadyExistError = isAlreadyExistErrorMsg(e);

          if (isAlreadyExistError) {
            setIsAccountAlreadyExistsError(true);
            window.scrollTo(0, 0);
            setPending(false);
            return;
          }

          const errors = formatBackendValidationErrors(e.fieldErrors);

          actions.setErrors(errors);
          scrollToElement(e.fieldErrors[0].property);
        } else {
          // eslint-disable-next-line no-console
          console.log(e);
        }

        setPending(false);
      }
    },
    [
      availableConsents?.ACCEPT_CREDIT_AGREEMENT.version,
      availableConsents?.UNDERSTANDING_AGREEMENT_CONFIRMED.version,
      availableConsents?.ACCEPT_NEWS.version,
      partialRegister,
      login,
      offer,
      createClientApplicationWithoutRisk,
      precheckApplication,
    ],
  );

  const handleLoginClick = useCallback((email: string) => {
    window.location.assign(
      `${DASHBOARD_LOGIN}?${import.meta.env.VITE_DASHBOARD_LOGIN_SEARCH_PARAMETER}=${window.btoa(
        email,
      )}`,
    );
  }, []);

  useEffect(() => {
    if (applicationCheckStatusData?.completed) {
      // We can stop polling the status check, when we have result
      setIsPollingApplicationStatus(false);
    }
  }, [applicationCheckStatusData]);

  useEffect(() => {
    if (!applicationData) {
      return;
    }
    identify(new IdentifyEvent({ application: applicationData }));
    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution === 'MANUAL'
    ) {
      navigate(MANUAL_REVIEW);
    }

    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution !== 'MANUAL'
    ) {
      navigate(PHONE_VERIFICATION);
      return;
    }

    if (applicationData.resolution === 'REJECTED') {
      navigate(REJECTED_OFFER);
    }
  }, [applicationData, navigate]);

  const handleAlreadyExistBoxClose = useCallback(() => {
    setIsAccountAlreadyExistsError(false);
  }, []);

  return {
    pending,
    handleSubmit,
    ioBlackBox,
    affiliateForm,
    isAccountAlreadyExistsError,
    affiliate,
    handleLoginClick,
    handleAlreadyExistBoxClose,
    availableConsents,
    availiableConsentsLoading: isLoading,
  };
}
